import { Button } from "@base-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, matchPath, useLocation } from "react-router-dom";
import {
  languageOptions,
  languageStorageKey,
  SupportedLanguage,
  supportedLanguages,
} from "../i18n";

type AuthenticatedLayoutProps = {
  email: string;
  onLogout: () => void;
  canManageClientCompanies: boolean;
  canMaintainEmployees: boolean;
  canManageUsers: boolean;
  meLoading: boolean;
  meError: string | null;
};

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  isActive ? "navLink active" : "navLink";

type Breadcrumb = {
  label: string;
  to?: string;
};

function AuthenticatedLayout({
  email,
  onLogout,
  canManageClientCompanies,
  canMaintainEmployees,
  canManageUsers,
  meLoading,
  meError,
}: AuthenticatedLayoutProps) {
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const normalizedLanguage = (i18n.resolvedLanguage ?? i18n.language)
    .split("-")[0]
    .toLowerCase();
  const activeLanguage = supportedLanguages.includes(
    normalizedLanguage as SupportedLanguage,
  )
    ? (normalizedLanguage as SupportedLanguage)
    : "is";

  const breadcrumbs = useMemo<Breadcrumb[]>(() => {
    if (pathname === "/") {
      return [{ label: t("Overview") }];
    }

    if (pathname === "/client-companies") {
      return [{ label: t("Client companies") }];
    }

    if (pathname === "/employees") {
      return [{ label: t("Employees") }];
    }

    if (pathname === "/employees/missing-name") {
      return [
        { label: t("Employees"), to: "/employees" },
        { label: t("Missing names") },
      ];
    }

    if (pathname === "/intake") {
      return [{ label: t("Create episode") }];
    }

    if (pathname === "/reports") {
      return [{ label: t("Reports") }];
    }

    if (pathname === "/users") {
      return [{ label: t("Users") }];
    }

    if (pathname === "/follow-ups") {
      return [{ label: t("Follow-ups") }];
    }

    if (pathname === "/intake/new") {
      return [{ label: t("Create episode"), to: "/intake" }, { label: t("New") }];
    }

    if (pathname === "/client-companies/new") {
      return [
        { label: t("Client companies"), to: "/client-companies" },
        { label: t("Create") },
      ];
    }

    if (pathname === "/employees/new") {
      return [
        { label: t("Employees"), to: "/employees" },
        { label: t("Create") },
      ];
    }

    if (matchPath("/client-companies/:id/edit", pathname)) {
      return [
        { label: t("Client companies"), to: "/client-companies" },
        { label: t("Edit") },
      ];
    }

    if (matchPath("/employees/:id/edit", pathname)) {
      return [{ label: t("Employees"), to: "/employees" }, { label: t("Edit") }];
    }

    if (matchPath("/employees/:id", pathname)) {
      return [
        { label: t("Employees"), to: "/employees" },
        { label: t("Details") },
      ];
    }

    if (matchPath("/episodes/:id", pathname)) {
      return [
        { label: t("Create episode"), to: "/intake" },
        { label: t("Details") },
      ];
    }

    return [];
  }, [pathname, t]);

  return (
    <main className="appShell">
      <aside className="sideNavigation" aria-label={t("Sidebar navigation")}>
        <Link to="/follow-ups" className="logoLink">
          Óvinnufærni
        </Link>

        <nav className="navGroup" aria-label={t("Main navigation")}>
          <NavLink className={navLinkClassName} to="/follow-ups">
            {t("Follow-ups")}
          </NavLink>

          {canMaintainEmployees ? (
            <NavLink className={navLinkClassName} to="/intake">
              {t("Create episode")}
            </NavLink>
          ) : (
            <span className="navLink disabled" aria-disabled="true">
              {t("Create episode")}
            </span>
          )}

          {canMaintainEmployees ? (
            <NavLink className={navLinkClassName} to="/reports">
              {t("Reports")}
            </NavLink>
          ) : (
            <span className="navLink disabled" aria-disabled="true">
              {t("Reports")}
            </span>
          )}
        </nav>

        <hr className="navDivider" />

        <nav className="navGroup" aria-label={t("Settings")}>
          <NavLink className={navLinkClassName} to="/employees" end>
            {t("Employees")}
          </NavLink>

          {canManageClientCompanies ? (
            <NavLink className={navLinkClassName} to="/client-companies">
              {t("Client companies")}
            </NavLink>
          ) : (
            <span className="navLink disabled" aria-disabled="true">
              {t("Client companies")}
            </span>
          )}

          {canManageUsers ? (
            <NavLink className={navLinkClassName} to="/users">
              {t("Users")}
            </NavLink>
          ) : null}
        </nav>

        <div className="sidebarBottom">
          <label className="languageSelector">
            <span className="languageSelectorLabel">{t("Language")}</span>
            <select
              className="inputControl languageSelectorControl"
              aria-label={t("Choose language")}
              value={activeLanguage}
              onChange={(event) => {
                const selectedLanguage = event.target.value as SupportedLanguage;
                window.localStorage.setItem(languageStorageKey, selectedLanguage);
                void i18n.changeLanguage(selectedLanguage);
              }}
            >
              {languageOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <p className="userEmail">{email}</p>

          <Button className="secondaryButton navLogoutButton" onClick={onLogout}>
            {t("Log out")}
          </Button>
        </div>
      </aside>

      <section className="contentArea">
        {breadcrumbs.length > 0 ? (
          <nav className="breadcrumbs" aria-label={t("Breadcrumb")}>
            {breadcrumbs.map((crumb, index) => (
              <span className="breadcrumbItem" key={`${crumb.label}-${index}`}>
                {crumb.to ? (
                  <Link className="breadcrumbLink" to={crumb.to}>
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="breadcrumbCurrent" aria-current="page">
                    {crumb.label}
                  </span>
                )}

                {index < breadcrumbs.length - 1 ? (
                  <span className="breadcrumbSeparator" aria-hidden="true">
                    /
                  </span>
                ) : null}
              </span>
            ))}
          </nav>
        ) : null}

        {meLoading ? <p className="hintText">{t("Checking role access...")}</p> : null}

        {meError ? (
          <p className="errorMessage" role="alert">
            {t("Unable to load role profile:")} {meError}
          </p>
        ) : null}

        <Outlet />
      </section>
    </main>
  );
}

export default AuthenticatedLayout;
